import React from 'react';
import {Tooltip} from '@/components/tooltips';
import {Parser} from 'expr-eval';
import {ChartType} from '@/views/storyboard/utils/chartConstants';
import {AggregationType, ChartOutput} from 'entities/chart';
import TrendValueString from '@/views/storyboard/charts/utils/TrendValueString';
import {BigNumberChartData} from 'entities/chartData';
import {withTranslation, WithTranslation} from 'react-i18next';
import i18n from 'i18n';
import {ChartFooterData, shouldHideChartTotal} from '@/views/storyboard/charts/utils/chartFooterData';

const defaultFontSize = 80;
const roundBigNumberTotal = (value: number): number => Math.round(value * 100) / 100;

interface Props extends WithTranslation {
    data: BigNumberChartData;
    scalingFactor: string;
    comparablePeriod: boolean;
    outputType?: ChartOutput;
    aggregationType?: AggregationType;
    queryDuration?: string;
    chartSettingsView?: boolean;
    onPreviewFooterChange?: (footerData: ChartFooterData) => void;
}

interface State {
    fontSize: number;
    containerWidth: number;
    containerHeight: number;
}


class BigNumberChartWrapper extends React.PureComponent<Props, State> {
    textSpanRef = React.createRef<HTMLHeadingElement>();
    containerRef = React.createRef<HTMLDivElement>();
    private resizeObserver: ResizeObserver | null = null;
    
    setTextRef = (element: HTMLHeadingElement | null) => {
        (this.textSpanRef as React.MutableRefObject<HTMLHeadingElement | null>).current = element;
        if (element && this.state.fontSize) {
            element.style.setProperty('font-size', `${this.state.fontSize}px`, 'important');
        }
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            fontSize: defaultFontSize,
            containerWidth: 0,
            containerHeight: 0,
        };
    }

    componentDidMount() {
        this.setupResizeObserver();
        requestAnimationFrame(() => {
            this.measureContainer();
            this.adjustFontSize();
            if (this.textSpanRef.current) {
                this.textSpanRef.current.style.setProperty('font-size', `${this.state.fontSize}px`, 'important');
            }
        });
        this.reportFooterData();
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const needsAdjustment = 
            prevState.containerWidth !== this.state.containerWidth ||
            prevState.containerHeight !== this.state.containerHeight ||
            prevProps.data?.count !== this.props.data?.count ||
            prevProps.data?.comparableCount !== this.props.data?.comparableCount ||
            prevProps.comparablePeriod !== this.props.comparablePeriod ||
            prevProps.scalingFactor !== this.props.scalingFactor;

        if (needsAdjustment) {
            requestAnimationFrame(() => {
                this.adjustFontSize();
            });
        }

        this.reportFooterData();
        
        if (this.textSpanRef.current && this.state.fontSize) {
            this.textSpanRef.current.style.setProperty('font-size', `${this.state.fontSize}px`, 'important');
        }
    }

    componentWillUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }

    private setupResizeObserver() {
        if (!this.containerRef.current) return;

        this.resizeObserver = new ResizeObserver(() => {
            this.measureContainer();
        });
        this.resizeObserver.observe(this.containerRef.current);
    }

    private measureContainer() {
        if (!this.containerRef.current) return;

        const {width, height} = this.containerRef.current.getBoundingClientRect();
        const roundedWidth = Math.round(width);
        const roundedHeight = Math.round(height);

        if (roundedWidth !== this.state.containerWidth || roundedHeight !== this.state.containerHeight) {
            this.setState({
                containerWidth: roundedWidth,
                containerHeight: roundedHeight,
            });
        }
    }

    adjustFontSize() {
        if (!this.textSpanRef.current) {
            return;
        }

        const {containerWidth, containerHeight, fontSize: currentFontSize} = this.state;
        if (containerWidth === 0 || containerHeight === 0) {
            return;
        }

        const {comparablePeriod} = this.props;
        const trendHeight = comparablePeriod ? 40 : 0;
        const padding = 20;
        const availableHeight = Math.max(containerHeight - trendHeight - padding, 20);
        const availableWidth = Math.max(containerWidth - padding, 20);

        const element = this.textSpanRef.current;
        let fontSize = defaultFontSize;
        const minFontSize = 10;

        while (fontSize >= minFontSize) {
            element.style.setProperty('font-size', `${fontSize}px`, 'important');
            void element.offsetHeight;
            
            const rect = element.getBoundingClientRect();
            const textWidth = rect.width;
            const textHeight = rect.height;

            if (textWidth <= availableWidth && textHeight <= availableHeight) {
                break;
            }
            fontSize -= 2;
        }

        const newFontSize = Math.max(fontSize, minFontSize);
        if (currentFontSize !== newFontSize) {
            this.setState({fontSize: newFontSize}, () => {
                if (this.textSpanRef.current) {
                    this.textSpanRef.current.style.setProperty('font-size', `${newFontSize}px`, 'important');
                }
            });
        } else if (this.textSpanRef.current) {
            this.textSpanRef.current.style.setProperty('font-size', `${newFontSize}px`, 'important');
        }
    }

    private reportFooterData() {
        const {chartSettingsView, onPreviewFooterChange, data, queryDuration = '0', comparablePeriod, outputType, aggregationType} = this.props;
        if (!chartSettingsView || !onPreviewFooterChange || !data) {
            return;
        }

        const {count, comparableCount} = data;
        let trendValue: string;
        if (comparableCount > 0) {
            trendValue = (count / comparableCount * 100 - 100).toFixed(2);
        } else if (count > 0) {
            trendValue = (100).toFixed(2);
        } else {
            trendValue = (0).toFixed(2);
        }

        onPreviewFooterChange({
            total: roundBigNumberTotal(count),
            totalComparable: roundBigNumberTotal(comparableCount),
            trendValue,
            queryDuration,
            showTrend: comparablePeriod && parseFloat(trendValue) !== 0,
            hideTotal: shouldHideChartTotal(outputType, aggregationType),
        });
    }

    render() {
        const {
            data,
            scalingFactor,
            comparablePeriod,
            outputType,
            queryDuration = '0',
            chartSettingsView = false,
            t
        } = this.props;
        const {fontSize} = this.state;

        function bigNumFormatter(num: number): string {
            const roundedNum = Math.round(num * 100) / 100;

            // Nine Zeroes for Billions
            if (Math.abs(Number(roundedNum)) >= 1.0e+9) {
                return `${(Math.abs(Number(roundedNum)) / 1.0e+9).toFixed(2)}B`;
            }
            // Six Zeroes for Millions
            if (Math.abs(Number(roundedNum)) >= 1.0e+6) {
                return `${(Math.abs(Number(roundedNum)) / 1.0e+6).toFixed(2)}M`;
            }
            // Three Zeroes for Thousands
            if (Math.abs(Number(roundedNum)) >= 1.0e+3) {
                return `${(Math.abs(Number(roundedNum)) / 1.0e+3).toFixed(2)}k`;
            }
            return Math.abs(Number(roundedNum)).toString();
        }

        const {count, comparableCount} = data;

        let trendValue: string;
        if (comparableCount > 0) {
            trendValue = (count / comparableCount * 100 - 100).toFixed(2);
        } else if (count > 0) {
            trendValue = (100).toFixed(2);
        } else {
            trendValue = (0).toFixed(2);
        }

        let scaledCount = count;
        if (scalingFactor.length && outputType === 'terms_metric') {
            try {
                scaledCount = Parser.evaluate(`count * (${scalingFactor})`, {count});
            } catch {
                scaledCount = 0;
            }
        }

        scaledCount = scaledCount % 1 !== 0 ? parseFloat(scaledCount.toFixed(2)) : scaledCount;

        const countFormatted = bigNumFormatter(scaledCount);

        return (
            <>
                <div
                    ref={this.containerRef}
                    className='big-number-chart'
                    style={{
                        fontSize,
                        flex: 1,
                        minHeight: 0,
                        width: '100%',
                        margin: 0,
                    }}
                >
                    <Tooltip
                        content={scaledCount.toString()}
                        placement="bottom"
                    >
                        <h1 
                            ref={this.setTextRef} 
                            style={{
                                fontSize: `${fontSize}px`,
                                margin: 0,
                                lineHeight: 1,
                            }}
                        >
                            {countFormatted}
                        </h1>
                    </Tooltip>

                    {
                        comparablePeriod && !chartSettingsView ? (
                            <h6 className='d-flex align-items-center' style={{marginTop: '20px', fontSize: fontSize / 6}}>
                                <TrendValueString
                                    value={trendValue}
                                    type={ChartType.BigNumber}
                                    comparableValue={comparableCount}
                                />
                            </h6>
                        ) : null
                    }
                </div>
                {!chartSettingsView ? (
                    <div className="chart-query-time-indicator">
                        <p>
                            {i18n.t('table_chart_query_duration.text', {queryDuration})}
                        </p>
                    </div>
                ) : null}
            </>
        );
    }
}

export default withTranslation()(BigNumberChartWrapper);
